import { Button, Col, DatePicker, Form, Input, Row, Select } from "antd";
import { Formik, FormikProps } from "formik";
import moment from "moment";
import { useEffect, useRef, useState } from "react";

import { categoryApi, regionApi, subRegionApi, toArray, vendorApi } from "../../../../Services";
import { usePanelSettings } from "../../../../Hooks/usePanelSettings";
import { allowedDomainEmail, permittedDomainsMessage } from "../../../../Utils/Constant";
import { usePanelOrgTree } from "../../../../Hooks/usePanelOrgTree";
import { InlineEdit, WorkOrderFormValues, procurementApproaches } from "../../Constants";
import styles from "../../WorkOrder.module.scss";

export interface TemplateOption {
  templateId: string;
  templateName: string;
}

interface Props {
  values: WorkOrderFormValues;
  /** Fetched once by the wizard shell from GET /template. */
  templates: TemplateOption[];
  onNext: (values: WorkOrderFormValues) => void;
  /* Passed the values on screen: this step holds them in Formik and only
     lifts them on Next, so a draft saved here would otherwise write whatever
     the wizard had before the step was touched. */
  onSaveDraft: (values: WorkOrderFormValues) => void;
  isSavingDraft?: boolean;
  /** A submitted work order: who raised it, for which part of the business,
   *  and the procurement route are settled, so those fields are read-only. */
  lockOrgFields?: boolean;
  /** A new work order: Date Request is today and not editable. */
  lockDateRequest?: boolean;
  /** When given, only these fields can be changed; every other one is shown
      read-only (a submitted work order: PR No. and the two dates). */
  editableFields?: (keyof WorkOrderFormValues)[];
  inlineEdit?: InlineEdit;
  /** Mark every field touched on arrival — Submit sent the user back here with fields missing. */
  showErrors?: boolean;
}

/**
 * The Project Information checks, field -> message. Shared with the wizard's
 * final Submit: Next no longer runs them, so Submit is where a missing
 * mandatory field is caught.
 */
export const validateProjectInformation = (formValues: WorkOrderFormValues): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!formValues.requesterName) errors.requesterName = "Requester Name is required";
  if (!formValues.requesterEmail) {
    errors.requesterEmail = "Requester Email is required";
  } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formValues.requesterEmail)) {
    errors.requesterEmail = "Enter a valid email";
  } else if (!allowedDomainEmail.test(formValues.requesterEmail.trim())) {
    // Shape is fine but the domain is not one this environment permits.
    errors.requesterEmail = permittedDomainsMessage;
  }
  if (!formValues.dateRequest) errors.dateRequest = "Date Request is required";
  if (!formValues.jobTitle) errors.jobTitle = "Job Title is required";
  if (!formValues.subsidiary) errors.subsidiary = "Subsidiary/Division is required";
  if (!formValues.costCentre) errors.costCentre = "Cost centre is required";
  if (!formValues.department) errors.department = "Department is required";
  /* PR No., Commencement Date and Completion Date are optional per the
     BRD — they were required here, which stopped a work order that
     legitimately has none of them from leaving this step. The ordering
     rule still applies whenever both dates are given. */
  if (!formValues.background) errors.background = "Background is required";
  if (!formValues.scopeOfWork) errors.scopeOfWork = "Scope of work is required";
  if (!formValues.budgetStatus) errors.budgetStatus = "Budget Status is required";
  if (
    formValues.completionDate &&
    formValues.commencementDate &&
    moment(formValues.completionDate).isBefore(moment(formValues.commencementDate))
  ) {
    errors.completionDate = "Completion date cannot be before the Commencement Date";
  }
  if (!formValues.contractCategory) {
    errors.contractCategory = "Work Order Contract Category is required";
  }
  if (!formValues.region) errors.region = "Region is required";
  if (!formValues.subRegion) errors.subRegion = "Sub Region is required";
  if (!formValues.procurementApproach) {
    errors.procurementApproach = "Procurement Approach is required";
  }
  if (!formValues.vendor) errors.vendor = "Vendor is required";
  return errors;
};

/** Required before leaving step 1; the rest are checked at Submit. */
const STEP_REQUIRED: (keyof WorkOrderFormValues)[] = ["subsidiary", "costCentre", "region", "subRegion", "contractCategory"];

/**
 * Step 1 — Project Information.
 *
 * Formik + antd `Form layout="vertical"` with `validateStatus`/`help` wired off
 * `touched`/`errors`, matching AddSORId. Subsidiary/Division and Cost Centre
 * come from what Panel Contract has configured (`usePanelOrgTree`) rather than
 * the ePOD org tree, so only entries set up under Approval Management are
 * offered here.
 */
export const ProjectInformation = ({
  values,
  templates,
  onNext,
  onSaveDraft,
  isSavingDraft,
  lockOrgFields = false,
  lockDateRequest = false,
  editableFields,
  inlineEdit,
  showErrors = false,
}: Props) => {
  const locked = (field: keyof WorkOrderFormValues) => !!editableFields && !editableFields.includes(field);
  /* Reference data. Region/Category are flat lists; Sub Region depends on the
     chosen Region, and the vendor list depends on Region + Procurement
     Approach (GET /vendor/procurement-vendors), so those are fetched on
     demand rather than up front. */
  const [regions, setRegions] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [subRegions, setSubRegions] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  /* Emergency is only offered when Settings enables it. Unknown counts as
     allowed, so an unreachable settings call never blocks a legitimate
     choice. */
  const { emergencyEnabled } = usePanelSettings();
  /* Subsidiary/Division and Cost Centre as configured in Panel Contract. */
  const { subsidiaries, costCentresFor } = usePanelOrgTree();

  useEffect(() => {
    regionApi.getAll().then((rows: any) => setRegions(toArray(rows))).catch(() => setRegions([]));
  }, []);

  /* Sub Region and Vendor are normally fetched by the Region / Approach
     cascade, so coming back to this step — from Review & Submit, or into an
     existing work order — left those lists empty. A Select with no matching
     option falls back to printing its value, which is the id, so the fields
     showed a uuid instead of the name. Reloading them on mount for whatever
     is already chosen restores the labels. */
  /* A saved work order carries Region, Sub Region and Category by NAME (the
     payload sends names), but the lookups below and the selects work by id —
     reopening one asked for `/subRegion/region/JNT 1` and got nothing. The
     region is resolved against the region list before anything is loaded. */
  const regionId =
    regions.find((region: any) => region.id === values.region || region.regionName === values.region)?.id ??
    values.region;
  useEffect(() => {
    if (!regions.length) return;
    if (regionId) loadCategories(regionId, values);
    if (regionId) loadSubRegions(regionId);
    if (regionId && values.procurementApproach) {
      loadVendors(regionId, values.procurementApproach, categoryKey(values.contractCategory));
    }
    // eslint-disable-next-line
  }, [regionId, values.procurementApproach, regions.length]);

  /* …and the form's own value follows: once each list is in, a name held in
     the field becomes that option's id, so the select shows it as chosen. */
  const formik = useRef<FormikProps<WorkOrderFormValues>>(null);
  const nameToId = (field: keyof WorkOrderFormValues, list: any[], nameKey: string) => {
    const current = formik.current?.values[field];
    const match = current && list.find((row: any) => row.id && row.id !== current && row[nameKey] === current);
    if (match) formik.current?.setFieldValue(field, match.id, false);
  };
  useEffect(() => nameToId("region", regions, "regionName"), [regions]);
  useEffect(() => nameToId("subRegion", subRegions, "subRegionName"), [subRegions]);
  useEffect(() => nameToId("contractCategory", categories, "categoryName"), [categories]);

  /**
   * Normal is always available; only Emergency is governed by Settings.
   *
   * The test used to keep Emergency and drop everything else, which left the
   * dropdown holding Emergency alone — and holding nothing at all when the
   * toggle was off, so no work order could be completed.
   */
  const availableApproaches = procurementApproaches.filter(
    (approach) => approach.value !== "EMERGENCY" || emergencyEnabled === true
  );

  /**
   * Catalog names arrive padded — the live region list holds entries such as
   * `"test_1           "` — which renders ragged in a dropdown and would be
   * stored that way on the work order. Every label goes through here.
   */
  /** Read-only fields are shaded so it is obvious they cannot be typed into. */
const readOnlyField = { background: "#f5f5f5", cursor: "not-allowed" };

/**
 * How dates are shown and typed.
 *
 * Display only — the value kept in the form and sent to the API stays
 * ISO (`YYYY-MM-DD`), which is what the work-order endpoints take.
 */
const DATE_DISPLAY = "DD-MM-YYYY";

const label = (value: any) => String(value ?? "").trim();

/**
 * Options read A–Z. Every list here arrives in whatever order the API stores
 * it, so each dropdown is sorted on the text the user actually sees.
 */
const sortedBy = <T,>(items: T[] | undefined, text: (item: T) => any): T[] =>
  [...(items ?? [])].sort((a, b) =>
    label(text(a)).localeCompare(label(text(b)), undefined, { sensitivity: "base", numeric: true })
  );

/**
 * The vendors in a procurement-vendors answer. Now `{ editable, selectedVendor,
 * vendors }`; older builds sent a list, a page or a single vendor.
 */
const asVendorList = (result: any): any[] => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.vendors)) return result.vendors;
  if (Array.isArray(result?.records)) return result.records;
  return result && typeof result === "object" && (result.id || result.vendorName) ? [result] : [];
};

/** The vendor the rotation recommends, when the answer names one. */
const recommendedVendor = (result: any): any =>
  result?.selectedVendor && (result.selectedVendor.id || result.selectedVendor.vendorName)
    ? result.selectedVendor
    : undefined;

/* Vendors now say `isActive`; older records said `active`. Either false drops it. */
const isActiveVendor = (vendor: any) => vendor?.isActive !== false && vendor?.active !== false;

const NORMAL_ROTATION = "NORMAL_ROTATION";

  /**
   * The selects are keyed by id so the cascades have a real FK to query with,
   * but an id is not a label — this reads the name back off the loaded list so
   * it can be stored alongside and shown on Review & Submit.
   */
  const nameOf = (rows: any[], value: string, idKey: string, nameKey: string) =>
    label(rows.find((row: any) => (row[idKey] ?? row[nameKey]) === value)?.[nameKey] ?? value);

  /* What procurement-vendors and the SOR lookup take for a category: its
     code (PW) where the catalog has one, else its name. */
  const categoryKey = (categoryId?: string) => {
    const category = categories.find((entry: any) => entry.id === categoryId || entry.categoryName === categoryId);
    return category?.categoryCode || category?.categoryName || "";
  };

  /**
   * The Category dropdown offers only the chosen region's categories (v2.1: a
   * category belongs to a region), so Region is picked first. A work order
   * saved against a category outside that list keeps it as an option, so the
   * field still shows its name rather than an id.
   */
  const loadCategories = (regionId: string, current?: { contractCategory?: string; contractCategoryName?: string }) => {
    if (!regionId) return setCategories([]);
    categoryApi
      .byRegion(regionId)
      .then((rows: any) => {
        const list = toArray(rows);
        const saved = current?.contractCategory;
        const missing =
          saved && !list.some((category: any) => category.id === saved || category.categoryName === saved);
        setCategories(missing ? [...list, { id: saved, categoryName: current?.contractCategoryName || saved }] : list);
      })
      .catch(() => setCategories([]));
  };

  const loadSubRegions = (regionId: string) => {
    if (!regionId) return setSubRegions([]);
    subRegionApi
      .byRegion(regionId)
      .then((rows: any) => setSubRegions(toArray(rows)))
      .catch(() => setSubRegions([]));
  };

  /**
   * Vendors for the chosen Region + Procurement Approach.
   *
   * `procurement-vendors` is asked first, since it is the endpoint that knows
   * the procurement rules. It has been answering with nothing for regions that
   * plainly have vendors configured, which left the picker empty and the form
   * uncompletable, so an empty answer falls back to every vendor configured
   * against that region — the same list Manage Catalog shows.
   *
   * Inactive vendors are dropped from the fallback: the eligibility endpoint
   * would not have offered them either.
   */
  const loadVendors = (
    regionId: string,
    approach: string,
    category?: string,
    assign?: (vendor: any) => void
  ) => {
    if (!regionId || !approach) return setVendors([]);

    const fallback = () =>
      vendorApi
        .byRegion(regionId)
        .then((result: any) => setVendors(toArray(result).filter(isActiveVendor)))
        .catch(() => setVendors([]));

    if (approach !== NORMAL_ROTATION) {
      /* Emergency: the eligible vendors, nothing chosen — the user picks. */
      vendorApi
        .forProcurement(regionId, approach as any, category)
        .then((rows: any) => {
          const eligible = asVendorList(rows).filter(isActiveVendor);
          /* Not editable means the backend has decided: take its vendor. */
          const recommended = recommendedVendor(rows);
          if (rows?.editable === false && recommended && assign) {
            setVendors(eligible.length ? eligible : [recommended]);
            assign(recommended);
            return;
          }
          return eligible.length ? setVendors(eligible) : fallback();
        })
        .catch(fallback);
      return;
    }

    /* Normal (Rotation Basis): procurement-vendors answers with ONLY the
       vendor whose turn it is, and that one row used to become the whole
       dropdown. The dropdown now offers every active vendor in the region,
       with the rotation's vendor chosen by default — or, if the rotation has
       nobody for this region, the first vendor offered. */
    const sameVendor = (a: any, b: any) => (a.id ?? a.vendorName) === (b.id ?? b.vendorName);
    Promise.all([
      vendorApi
        .byRegion(regionId)
        .then((result: any) => toArray(result).filter(isActiveVendor))
        .catch(() => []),
      vendorApi
        .forProcurement(regionId, approach as any, category)
        .then((result: any) => ({
          list: asVendorList(result).filter(isActiveVendor),
          recommended: recommendedVendor(result),
          editable: result?.editable !== false,
        }))
        .catch(() => ({ list: [] as any[], recommended: undefined, editable: true })),
    ]).then(([regionVendors, rotation]: [any[], { list: any[]; recommended: any; editable: boolean }]) => {
      const offered = [...rotation.list, ...(rotation.recommended ? [rotation.recommended] : [])];
      const options = [...regionVendors];
      offered.forEach((vendor) => {
        if (!options.some((v) => sameVendor(v, vendor))) options.push(vendor);
      });
      setVendors(options);
      /* The backend's recommendation (`selectedVendor`) first; an older build
         answered with just the rotation's vendor; failing both, the first A–Z. */
      const pick = rotation.recommended ?? rotation.list[0] ?? sortedBy(options, (vendor: any) => vendor.vendorName)[0];
      if (pick && assign) assign(pick);
      /* The rotation's vendor is only the default; the user can always pick another. */
    });
  };

  return (
    <Formik<WorkOrderFormValues>
      innerRef={formik}
      enableReinitialize
      initialValues={values}
      initialTouched={showErrors ? Object.fromEntries(Object.keys(values).map((key) => [key, true])) : undefined}
      validateOnMount={showErrors}
      validate={validateProjectInformation}
      onSubmit={onNext}
    >
      {({ handleSubmit, handleChange, handleBlur, setFieldValue, setFieldTouched, values: v, touched, errors, isValid, dirty }) => {
        const err = (field: keyof WorkOrderFormValues) =>
          touched[field] && errors[field] ? (errors[field] as string) : undefined;

        /* The vendor rotation picked, written into the form like a choice.
           Name first and unvalidated, id last: Formik validates each
           setFieldValue against the values as they stood BEFORE the call, so
           validating after the name still saw the vendor empty and recorded
           "Vendor is required". A hand-picked vendor recovers on blur, but the
           rotation's field is locked, is never blurred — and Next stayed
           disabled with every field complete. */
        const assignVendor = (vendor: any) => {
          setFieldValue("vendorName", label(vendor.vendorName), false);
          setFieldValue("vendor", vendor.id ?? vendor.vendorName);
        };

        return (
          <Form
            layout="vertical"
            onFinish={handleSubmit}
          >
            {/* In place on the details page the form spans the card, like the
                read view it replaces; the wizard keeps its narrower column. */}
            <div className={inlineEdit ? undefined : styles.formBody}>
              {/* The Template picker was removed from this step. `templateId`
                  is still part of the payload, so the first configured
                  template is taken automatically — see the effect above. */}

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Requester Name"
                    validateStatus={err("requesterName") ? "error" : undefined}
                    help={err("requesterName")}
                  >
                    {/* The requester is whoever is signed in, so it is read
                        from the session rather than typed. */}
                    <Input
                      name="requesterName"
                      value={v.requesterName}
                      disabled
                      style={readOnlyField}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Requester Email"
                    validateStatus={err("requesterEmail") ? "error" : undefined}
                    help={err("requesterEmail")}
                  >
                    <Input
                      name="requesterEmail"
                      value={v.requesterEmail}
                      disabled
                      style={readOnlyField}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Date Request"
                    validateStatus={err("dateRequest") ? "error" : undefined}
                    help={err("dateRequest")}
                  >
                    <DatePicker
                      format={DATE_DISPLAY}
                      className="w-100"
                      style={{ width: "100%" }}
                      placeholder="Select Date"
                      value={v.dateRequest ? moment(v.dateRequest) : null}
                      disabled={lockDateRequest || locked("dateRequest")}
                      onChange={(date) =>
                        setFieldValue("dateRequest", date ? date.format("YYYY-MM-DD") : "")
                      }
                      onBlur={handleBlur}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Job Title"
                    validateStatus={err("jobTitle") ? "error" : undefined}
                    help={err("jobTitle")}
                  >
                    <Input
                      name="jobTitle"
                      disabled={locked("jobTitle")}
                      value={v.jobTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter Job Title"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Subsidiary/Division"
                    validateStatus={err("subsidiary") ? "error" : undefined}
                    help={err("subsidiary")}
                  >
                    <Select
                      showSearch
                      optionFilterProp="children"
                      placeholder="Choose"
                      value={v.subsidiary || undefined}
                      disabled={locked("subsidiary") || lockOrgFields}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                      onChange={(value: string) => {
                        setFieldValue("subsidiary", value);
                        setFieldValue("costCentre", "");
                      }}
                      onBlur={handleBlur}
                    >
                      {sortedBy(subsidiaries, (name: string) => name).map((name: string, index: number) => (
                        <Select.Option value={name} key={`${index}`}>
                          {name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Cost centre"
                    validateStatus={err("costCentre") ? "error" : undefined}
                    help={err("costCentre")}
                  >
                    <Select
                      showSearch
                      optionFilterProp="children"
                      placeholder="Choose"
                      value={v.costCentre || undefined}
                      disabled={locked("costCentre") || lockOrgFields || !v.subsidiary}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                      onChange={(value: string) => setFieldValue("costCentre", value)}
                      onBlur={handleBlur}
                    >
                      {sortedBy(costCentresFor([v.subsidiary]), (name: string) => name).map((name: string, index: number) => (
                        <Select.Option value={name} key={`${index}`}>
                          {name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Department"
                    validateStatus={err("department") ? "error" : undefined}
                    help={err("department")}
                  >
                    <Input
                      name="department"
                      disabled={locked("department")}
                      value={v.department}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter Department"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="PR No."
                    validateStatus={err("prNo") ? "error" : undefined}
                    help={err("prNo")}
                  >
                    <Input
                      name="prNo"
                      disabled={locked("prNo")}
                      value={v.prNo}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Full-width text areas with a counter — e-POD's Project Name /
                  Project Summary boxes (110px, 5000 / 50000 characters). */}
              <Row gutter={24}>
                <Col xs={24}>
                  <Form.Item required
                    label="Background"
                    validateStatus={err("background") ? "error" : undefined}
                    help={err("background")}
                  >
                    <Input.TextArea
                      name="background"
                      disabled={locked("background")}
                      value={v.background}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter Background text"
                      showCount
                      maxLength={5000}
                      style={{ height: 110 }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item required
                    label="Scope of work"
                    validateStatus={err("scopeOfWork") ? "error" : undefined}
                    help={err("scopeOfWork")}
                  >
                    <Input.TextArea
                      name="scopeOfWork"
                      disabled={locked("scopeOfWork")}
                      value={v.scopeOfWork}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter Scope of work"
                      showCount
                      maxLength={50000}
                      style={{ height: 110 }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Budget Status"
                    validateStatus={err("budgetStatus") ? "error" : undefined}
                    help={err("budgetStatus")}
                  >
                    <Input
                      name="budgetStatus"
                      disabled={locked("budgetStatus")}
                      value={v.budgetStatus}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter Budget Status"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Commencement Date"
                    validateStatus={err("commencementDate") ? "error" : undefined}
                    help={err("commencementDate")}
                  >
                    <DatePicker
                      format={DATE_DISPLAY}
                      style={{ width: "100%" }}
                      placeholder="Select Date"
                      value={v.commencementDate ? moment(v.commencementDate) : null}
                      disabled={locked("commencementDate")}
                      onChange={(date) =>
                        setFieldValue("commencementDate", date ? date.format("YYYY-MM-DD") : "")
                      }
                      onBlur={handleBlur}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Completion date"
                    validateStatus={err("completionDate") ? "error" : undefined}
                    help={err("completionDate")}
                  >
                    <DatePicker
                      format={DATE_DISPLAY}
                      style={{ width: "100%" }}
                      placeholder="Select Date"
                      value={v.completionDate ? moment(v.completionDate) : null}
                      disabled={locked("completionDate")}
                      disabledDate={(current) =>
                        !!v.commencementDate && current && current < moment(v.commencementDate).startOf("day")
                      }
                      onChange={(date) =>
                        setFieldValue("completionDate", date ? date.format("YYYY-MM-DD") : "")
                      }
                      onBlur={handleBlur}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Region"
                    validateStatus={err("region") ? "error" : undefined}
                    help={err("region")}
                  >
                    <Select
                      showSearch
                      optionFilterProp="children"
                      placeholder="Choose"
                      value={v.region || undefined}
                      disabled={locked("region") || lockOrgFields}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                      onChange={(value: string) => {
                        // Sub Region and Vendor both hang off Region — clear
                        // them so a stale pairing can never be submitted.
                        setFieldValue("region", value);
                        setFieldValue("regionName", nameOf(regions, value, "id", "regionName"));
                        setFieldValue("subRegion", "");
                        setFieldValue("subRegionName", "");
                        setFieldValue("vendor", "");
                        setFieldValue("vendorName", "");
                        /* Categories are per region, so the old one no longer applies. */
                        setFieldValue("contractCategory", "");
                        setFieldValue("contractCategoryName", "");
                        loadCategories(value);
                        loadSubRegions(value);
                        loadVendors(value, v.procurementApproach, "", assignVendor);
                      }}
                      onBlur={handleBlur}
                    >
                      {sortedBy(regions, (region: any) => region.regionName).map((region: any, index: number) => (
                        <Select.Option
                          value={region.id ?? region.regionName}
                          key={`${index}`}
                        >
                          {label(region.regionName)}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Work Order Contract Category"
                    validateStatus={err("contractCategory") ? "error" : undefined}
                    help={err("contractCategory")}
                  >
                    <Select
                      showSearch
                      optionFilterProp="children"
                      placeholder="Choose"
                      value={v.contractCategory || undefined}
                      disabled={locked("contractCategory") || lockOrgFields || !v.region}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                      onChange={(value: string) => {
                        setFieldValue("contractCategory", value);
                        setFieldValue(
                          "contractCategoryName",
                          nameOf(categories, value, "id", "categoryName")
                        );
                        /* The rotation is per category too, so a new category
                           re-asks for the recommended vendor. */
                        if (v.region && v.procurementApproach) {
                          setFieldValue("vendor", "");
                          setFieldValue("vendorName", "");
                          loadVendors(v.region, v.procurementApproach, categoryKey(value), assignVendor);
                        }
                      }}
                      onBlur={handleBlur}
                    >
                      {sortedBy(categories, (category: any) => category.categoryName).map((category: any, index: number) => (
                        <Select.Option
                          value={category.id ?? category.categoryName}
                          key={`${index}`}
                        >
                          {label(category.categoryName)}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Sub Region"
                    validateStatus={err("subRegion") ? "error" : undefined}
                    help={err("subRegion")}
                  >
                    <Select
                      showSearch
                      optionFilterProp="children"
                      placeholder="Choose"
                      value={v.subRegion || undefined}
                      disabled={locked("subRegion") || lockOrgFields || !v.region}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                      onChange={(value: string) => {
                        setFieldValue("subRegion", value);
                        setFieldValue(
                          "subRegionName",
                          nameOf(subRegions, value, "id", "subRegionName")
                        );
                      }}
                      onBlur={handleBlur}
                    >
                      {sortedBy(subRegions, (subRegion: any) => subRegion.subRegionName).map((subRegion: any, index: number) => (
                        <Select.Option
                          value={subRegion.id ?? subRegion.subRegionName}
                          key={`${index}`}
                        >
                          {label(subRegion.subRegionName)}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Procurement Approach"
                    validateStatus={err("procurementApproach") ? "error" : undefined}
                    help={err("procurementApproach")}
                  >
                    <Select
                      showSearch
                      optionFilterProp="children"
                      placeholder="Choose"
                      value={v.procurementApproach || undefined}
                      disabled={locked("procurementApproach") || lockOrgFields}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                      onChange={(value: string) => {
                        setFieldValue("procurementApproach", value);
                        setFieldValue("vendor", "");
                        setFieldValue("vendorName", "");
                        loadVendors(v.region, value, categoryKey(v.contractCategory), assignVendor);
                      }}
                      onBlur={handleBlur}
                    >
                      {sortedBy(availableApproaches, (approach) => approach.label).map((approach, index) => (
                        <Select.Option
                          value={approach.value}
                          key={`${index}`}
                        >
                          {approach.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item required
                    label="Vendor"
                    validateStatus={err("vendor") ? "error" : undefined}
                    help={err("vendor")}
                  >
                    <Select
                      showSearch
                      optionFilterProp="children"
                      placeholder="Choose"
                      value={v.vendor || undefined}
                      /* Pre-selected under Normal (Rotation Basis); open for an
                         override unless procurement-vendors says editable: false. */
                      disabled={locked("vendor") || lockOrgFields || !v.region || !v.procurementApproach}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                      onChange={(value: string) => {
                        // Same order as assignVendor — the id validates last.
                        setFieldValue("vendorName", nameOf(vendors, value, "id", "vendorName"), false);
                        setFieldValue("vendor", value);
                      }}
                      onBlur={handleBlur}
                    >
                      {/* A loaded work order holds the vendor ID, and the list
                          it would be resolved against is fetched from Region +
                          Approach — until that arrives the select has no
                          matching option and falls back to printing the raw
                          id. The saved name is offered as that option so the
                          field reads correctly from the moment it loads. */}
                      {v.vendor && !vendors.some((vendor: any) => (vendor.id ?? vendor.vendorName) === v.vendor) && (
                        <Select.Option
                          value={v.vendor}
                          key="loaded"
                        >
                          {label(v.vendorName) || v.vendor}
                        </Select.Option>
                      )}
                      {sortedBy(vendors, (vendor: any) => vendor.vendorName).map((vendor: any, index: number) => (
                        <Select.Option
                          value={vendor.id ?? vendor.vendorName}
                          key={`${index}`}
                        >
                          {label(vendor.vendorName)}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row
                justify="end"
                gutter={16}
                className={styles.stepActions}
              >
                {inlineEdit ? (
                  <>
                    <Col>
                      <Button onClick={inlineEdit.onCancel}>Cancel</Button>
                    </Col>
                    <Col>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={inlineEdit.isSaving}
                      >
                        Save
                      </Button>
                    </Col>
                  </>
                ) : (
                  <>
                    <Col>
                      <Button
                        loading={isSavingDraft}
                        onClick={() => onSaveDraft(v)}
                      >
                        Save as draft
                      </Button>
                    </Col>
                    <Col>
                      {/* Only Subsidiary/Division, Cost Centre, Region and Category
                          hold this step: the SOR lines and the approval chain
                          are drawn from them. Every other mandatory field is
                          enforced at the final Submit (validateProjectInformation). */}
                      <Button
                        type="primary"
                        onClick={() => {
                          const missing = STEP_REQUIRED.filter((field) => !v[field]);
                          if (!missing.length) return onNext(v);
                          missing.forEach((field) => setFieldTouched(field, true));
                        }}
                      >
                        Next
                      </Button>
                    </Col>
                  </>
                )}
              </Row>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
